import React, { useState } from 'react';
import { FaTimes, FaFilePdf, FaCheck, FaEnvelope, FaPlus, FaTrash, FaSpinner, FaPaperPlane } from 'react-icons/fa';
import { shareItineraryPdfEmail } from '../api/client';

const MAX_RECIPIENTS = 5;

const SharePdfModal = ({ requestId, itineraryTitle, onClose }) => {
  const [emails, setEmails] = useState(['']);
  const [personalMessage, setPersonalMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailsSent, setEmailsSent] = useState(0);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAddEmail = () => {
    if (emails.length < MAX_RECIPIENTS) {
      setEmails([...emails, '']);
    }
  };

  const handleRemoveEmail = (index) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      setEmails(newEmails);
    }
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Filter out empty emails and validate
    const validEmails = emails.filter(email => email.trim() !== '');

    if (validEmails.length === 0) {
      setError('Please enter at least one email address');
      return;
    }

    const invalidEmails = validEmails.filter(email => !validateEmail(email.trim()));
    if (invalidEmails.length > 0) {
      setError(`Invalid email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.join(', ')}`);
      return;
    }

    setLoading(true);

    try {
      const response = await shareItineraryPdfEmail(
        requestId,
        validEmails.map(e => e.trim()),
        false,
        personalMessage.trim() || null
      );

      setSuccess(true);
      setEmailsSent(response.emails_sent || validEmails.length);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.error === 'subscription_required') {
        setError(detail.message);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to send PDF. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-floating max-w-lg w-full max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-500 to-primary-600 rounded-t-2xl">
          <h3 className="text-xl font-bold text-white flex items-center gap-3">
            <FaFilePdf />
            Email PDF Itinerary
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
          >
            <FaTimes className="text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-8 animate-scale-in">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheck className="text-3xl text-green-600" />
              </div>
              <h4 className="text-xl font-bold text-neutral-800 mb-2">PDF Sent Successfully!</h4>
              <p className="text-neutral-600">
                Your itinerary has been sent to {emailsSent} recipient{emailsSent > 1 ? 's' : ''}.
              </p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <p className="text-neutral-600 mb-6">
                Send a PDF version of <strong>{itineraryTitle}</strong> directly to your travel companions' email.
              </p>

              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-6 animate-slide-down">
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email Inputs */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
                    <FaEnvelope className="text-primary-500" />
                    Recipient Emails (up to {MAX_RECIPIENTS})
                  </label>

                  <div className="space-y-2">
                    {emails.map((email, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                          placeholder={`Email address ${index + 1}`}
                          disabled={loading}
                          autoFocus={index === 0}
                          className="flex-1 px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        {emails.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveEmail(index)}
                            disabled={loading}
                            className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Remove email"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {emails.length < MAX_RECIPIENTS && (
                    <button
                      type="button"
                      onClick={handleAddEmail}
                      disabled={loading}
                      className="mt-2 flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
                    >
                      <FaPlus className="text-xs" />
                      Add another recipient
                    </button>
                  )}
                </div>

                {/* Personal Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-neutral-700 mb-2">
                    Personal Message (optional)
                  </label>
                  <textarea
                    id="message"
                    value={personalMessage}
                    onChange={(e) => setPersonalMessage(e.target.value)}
                    placeholder="Add a personal note to include with the PDF..."
                    disabled={loading}
                    maxLength={500}
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 outline-none transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-neutral-400 mt-1 text-right">
                    {personalMessage.length}/500 characters
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-primary-50 rounded-lg p-4">
                  <p className="text-sm text-primary-800">
                    <strong>What they'll receive:</strong> A beautifully formatted PDF of your itinerary attached to an email. No account needed to view the PDF.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 px-4 py-3 rounded-lg font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-3 rounded-lg font-medium text-white bg-gradient-primary shadow-primary hover:shadow-elevated hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <FaSpinner className="animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <FaPaperPlane />
                        Send PDF
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharePdfModal;
